import React, { MouseEvent, ReactNode, RefObject, useMemo } from 'react'

import { ColumnConfig, HeaderCellClickArgs, ResizedColumnsParams } from '../../DataGrid.types'
import { genericMemo, getColumnInfo, getColumnPinning, StickyController } from '../../utils'
import { HeaderCell } from '../HeaderCell'
import { HeaderGroupCell } from '../HeaderGroupCell'
import { useColumnGrouping } from './Header.hooks'

interface HeaderProps<RowType> {
  columns: ColumnConfig<RowType>[]
  columnGroupHeaderRenderer?: (groupId: string) => ReactNode
  gridId: string
  isResizable?: boolean
  onColumnResize: ({ columnKey, updatedColumnWidth }: ResizedColumnsParams) => void
  pinnedColumns: number
  pinnedColumnsRight: number
  stickyControllerRef: RefObject<StickyController>
  selectedColumnIndexes?: number[]
  onHeaderCellClick?: (cellClickArgs: HeaderCellClickArgs) => void
}
function _Header<RowType>({
  columns,
  columnGroupHeaderRenderer,
  gridId,
  isResizable,
  onColumnResize,
  pinnedColumns,
  pinnedColumnsRight,
  stickyControllerRef,
  selectedColumnIndexes,
  onHeaderCellClick,
}: HeaderProps<RowType>) {
  const { hasColumnGrouping, columnGroups } = useColumnGrouping(
    columns,
    pinnedColumns,
    pinnedColumnsRight,
  )

  const headersCells = useMemo(
    () =>
      columns.map(col => ({
        groupId: col.groupId,
        content: col.header,
        columnKey: col.columnKey,
        minWidth: col.minWidth,
      })),
    [columns],
  )

  const renderHeaderGroups = () =>
    columnGroups?.map(({ groupId, colEnd, colStart, columnPinning }, index) => {
      return (
        <HeaderGroupCell
          key={groupId}
          colStart={colStart}
          colEnd={colEnd}
          columnPinning={columnPinning}
          groupId={groupId}
          index={index}
          stickyControllerRef={stickyControllerRef}
          columnGroupHeaderRenderer={columnGroupHeaderRenderer}
          isLastGroup={index === columnGroups?.length - 1}
        />
      )
    }) ?? null

  const renderHeaderCells = () =>
    headersCells.map(({ columnKey, content, groupId, minWidth }, columnIndex) => {
      const {
        isLastColumn,
        isLastPinnedLeftColumn,
        isLastColumnInTheGroup,
        isFirstPinnedRightColumn,
      } = getColumnInfo({
        columnIndex,
        columns,
        pinnedColumns,
        pinnedColumnsRight,
      })

      const handleClickHeaderCell = (event: MouseEvent<HTMLDivElement>) =>
        onHeaderCellClick?.({
          columnKey,
          columnIndex,
          groupId,
          target: event.currentTarget,
          shiftKey: event.shiftKey,
        })
      const isColumnSelected = selectedColumnIndexes?.some(index => index === columnIndex)
      return (
        <HeaderCell
          columnIndex={columnIndex}
          columnKey={columnKey}
          columnMinWidth={minWidth}
          columnPinning={getColumnPinning(columnIndex, pinnedColumns, pinnedColumnsRight, columns)}
          gridId={gridId}
          groupId={groupId}
          headerSpan={hasColumnGrouping && !groupId}
          isLastColumn={isLastColumn}
          isResizable={isResizable}
          isColumnSelected={isColumnSelected}
          key={columnKey}
          onColumnResize={onColumnResize}
          stickyControllerRef={stickyControllerRef}
          withLeftBorder={isFirstPinnedRightColumn}
          withRightBorder={isLastColumnInTheGroup || isLastPinnedLeftColumn}
          onClick={handleClickHeaderCell}
        >
          {content}
        </HeaderCell>
      )
    })

  return (
    <>
      {renderHeaderGroups()}
      {renderHeaderCells()}
    </>
  )
}

export const Header = genericMemo(_Header)
